import { Pressable, type PressEvent } from '../../primitives/Pressable';
import { Text } from '../../primitives/Text';
import { View } from '../../primitives/View';
import { useTheme } from '../../theme';
import { cn } from '../../utils/cn';
import { Checkbox } from '../Checkbox';
import { Radio, type RadioSize } from '../Radio';
import {
  CHOICE_INPUT_ICON_SIZE_FOR,
  resolveChoiceInputIconColor,
} from './ChoiceInput.icons';
import type { ChoiceInputProps } from './ChoiceInput.types';
import {
  choiceInputDescriptionVariants,
  choiceInputImageVariants,
  choiceInputLabelVariants,
  choiceInputTitleVariants,
  choiceInputVariants,
} from './ChoiceInput.variants';

/**
 * Cross-platform ChoiceInput — a selectable option row with a presentational
 * Checkbox or Radio in the right slot. Renders identically on web and React
 * Native. Mirrors the Figma "Flashka Design System" frames `3420:8272`
 * (light) / `4225:802` (dark). See `ChoiceInput.variants.ts` for the
 * per-state spec + anatomy.
 *
 * Controlled-only — the caller owns the boolean:
 *
 * @example
 * ```tsx
 * import { ChoiceInput } from '@flashka/shared/ui';
 *
 * const [picked, setPicked] = useState(false);
 * <ChoiceInput label="Mitochondria" checked={picked} onCheckedChange={setPicked} />
 *
 * // Neutral selected treatment (no brand-accent re-tone when checked)
 * <ChoiceInput variant="secondary" label="Ribosome" checked={picked2} onCheckedChange={setPicked2} />
 *
 * // Radio group with descriptions
 * <ChoiceInput
 *   control="radio"
 *   size="l"
 *   label="Pro plan"
 *   description="Unlimited decks and AI generations"
 *   checked={plan === 'pro'}
 *   onCheckedChange={() => setPlan('pro')}
 * />
 * ```
 */
export function ChoiceInput({
  label,
  description,
  control = 'checkbox',
  variant = 'primary',
  checked = false,
  onCheckedChange,
  size = 'm',
  icon: Icon,
  image,
  disabled = false,
  haptic = 'selection',
  className,
  accessibilityLabel,
  testID,
  webProps,
  nativeProps,
  onHoverIn,
  onHoverOut,
}: ChoiceInputProps) {
  const { isDark } = useTheme();

  // CVA's VariantProps keep `null` in the union even with a default value, so
  // coalesce before indexing the icon-size map.
  const resolvedSize = size ?? 'm';
  // Selected primary rows re-tone every text/icon to primary-950 (Figma
  // "Selected -Primary accent"); selected secondary rows keep the neutral
  // colors, so only this combination needs overrides.
  const selectedPrimary = checked && (variant ?? 'primary') === 'primary';
  const withDescription = description !== undefined;
  // Figma has no icon+image cell — the image wins when both are passed.
  const leftSlot = image !== undefined ? 'image' : Icon ? 'icon' : 'none';

  // Rows with a description or an image render the label in the bolder title
  // style (Figma styles both identically); plain inline rows use the label style.
  const labelClass = cn(
    withDescription || leftSlot === 'image'
      ? choiceInputTitleVariants({ size })
      : choiceInputLabelVariants({ size }),
    selectedPrimary && 'text-primary-950',
  );

  // Right-slot control size per row size (Figma: 16/16/20/20/24px).
  const controlSize: RadioSize =
    resolvedSize === '2xl'
      ? 'l'
      : resolvedSize === 's' || resolvedSize === 'm'
        ? 's'
        : 'm';

  const handlePress = (event: PressEvent) => {
    if (disabled) return;
    // Radio rows select but never toggle off, matching the shared Radio.
    onCheckedChange?.(control === 'radio' ? true : !checked, event);
  };

  const mergedWebProps = { 'aria-checked': checked, ...(webProps ?? {}) };
  const mergedNativeProps = {
    accessibilityState: { checked, disabled },
    ...(nativeProps ?? {}),
  };

  return (
    <Pressable
      className={cn(
        choiceInputVariants({
          size,
          leftSlot,
          withDescription,
          variant,
          selected: checked,
        }),
        className,
      )}
      disabled={disabled}
      haptic={haptic}
      onPress={handlePress}
      onHoverIn={onHoverIn}
      onHoverOut={onHoverOut}
      accessibilityRole={control}
      accessibilityLabel={accessibilityLabel ?? label}
      testID={testID}
      webProps={mergedWebProps}
      nativeProps={mergedNativeProps}>
      {leftSlot === 'image' && (
        <View className={choiceInputImageVariants({ size })}>{image}</View>
      )}
      {leftSlot === 'icon' &&
        Icon &&
        (withDescription ? (
          // With a description the icon top-aligns with the title line
          // (Figma 3420:8215: self-stretch column, 6px top offset).
          <View className="flex h-full flex-col items-start self-stretch pt-1.5">
            <Icon
              color={resolveChoiceInputIconColor(isDark, selectedPrimary)}
              size={CHOICE_INPUT_ICON_SIZE_FOR[resolvedSize]}
            />
          </View>
        ) : (
          <Icon
            color={resolveChoiceInputIconColor(isDark, selectedPrimary)}
            size={CHOICE_INPUT_ICON_SIZE_FOR[resolvedSize]}
          />
        ))}
      <View className="min-w-0 flex-1 flex-col items-start justify-center">
        <Text className={cn('w-full text-left', labelClass)}>{label}</Text>
        {withDescription && (
          <Text
            className={cn(
              'w-full text-left',
              choiceInputDescriptionVariants({ size }),
              selectedPrimary && 'text-primary-950',
            )}>
            {description}
          </Text>
        )}
      </View>
      {/* Presentational — the row owns the checkbox/radio semantics. The
          checkbox type follows the row variant (primary → primary, secondary
          → default); the Radio has a single brand-purple style in Figma. */}
      {control === 'radio' ? (
        <Radio checked={checked} size={controlSize} interactive={false} />
      ) : (
        <Checkbox
          checked={checked}
          variant={(variant ?? 'primary') === 'primary' ? 'primary' : 'default'}
          size={controlSize}
          interactive={false}
        />
      )}
    </Pressable>
  );
}
(ChoiceInput as { displayName?: string }).displayName = 'ChoiceInput';
